/**
 * planDischargeModel — PURE state model for the S4 "Plan discharge" dialog
 * (build-plan FE-2, C2 frozen contract). No React, no API calls: given the
 * `Planned date`/`Planned time` fields the desk typed plus `admitted_at`, it
 * mirrors the server's own two 422 predicates (`PLANNED_DISCHARGE_BEFORE_ADMISSION`,
 * `PLANNED_DISCHARGE_TOO_FAR_AHEAD`) CLIENT-SIDE so `Save plan` can be
 * disabled with a reason before a round-trip, and re-runs identically
 * whichever field changed (AC-I2/AC-I3 — neither field silently rewrites the
 * other).
 *
 * `formatDischargeDateTimeIST`/`formatPlannedDischargeChip` are the ONE
 * "19 Sep 2026, 11:00 AM IST" formatter — reused VERBATIM by FE-3's desk row
 * chip and FE-4's running-bill header chip / Complete-step confirm line
 * prefix (build-plan §Tasks FE-2/FE-3/FE-4). Built with fixed-offset IST
 * arithmetic (same technique `utils/formatIST.js` uses for its own
 * contract-exact helpers), never `toLocaleDateString(...,{month:'short'})` —
 * Node/ICU renders that as "Sept", not the ratified "Sep".
 */

import {
  PLAN_DISCHARGE_MAX_AHEAD_DAYS,
  PLAN_DISCHARGE_ERROR_CODE,
  isTerminal,
  dodIsConfirmed,
} from '../../../constants/admission';
import { utcIsoToISTInputValue, istInputValueToUtcIso } from '../../../utils/formatIST';

// Ratified verbatim (stories.md Screen B AC-I2/AC-I4).
export const PLAN_DISCHARGE_BEFORE_ADMISSION_ERROR = 'Planned discharge cannot be before the admission date and time.';
export const PLAN_DISCHARGE_TOO_FAR_AHEAD_ERROR = `Planned discharge cannot be more than ${PLAN_DISCHARGE_MAX_AHEAD_DAYS} days ahead.`;
// Ratified verbatim (stories.md Screen B AC-I5).
export const DISCHARGE_PLAN_CLEARED_TOAST = 'Discharge plan cleared.';

/** "YYYY-MM-DDTHH:mm" IST wall-clock value -> separate `{date, time}` input values. */
export function splitISTValue(istValue) {
  if (!istValue) return { date: '', time: '' };
  const [date, time] = istValue.split('T');
  return { date: date || '', time: time || '' };
}

/** Separate `<input type="date">`/`<input type="time">` values -> one IST wall-clock value. */
export function joinISTValue(date, time) {
  if (!date || !time) return '';
  return `${date}T${time}`;
}

/**
 * defaultPlanFields — Screen B AC-I1: with no existing plan, both fields
 * default to NOW (today's date + current time, IST). With an existing plan
 * (the dialog is reachable to EDIT one at any pre-completion status, AC3),
 * prefill from it instead of resetting to now.
 */
export function defaultPlanFields(plannedDischargeAt, now = new Date()) {
  const iso = plannedDischargeAt || now.toISOString();
  return splitISTValue(utcIsoToISTInputValue(iso));
}

/**
 * validatePlanDischarge — the ONE predicate AC-I2/AC-I3/AC-I4 re-run on every
 * `Planned date`/`Planned time` edit, in either order. Mirrors the server's
 * two 422 guards (`>= admitted_at`, `<= now + PLAN_DISCHARGE_MAX_AHEAD_DAYS`)
 * so `Save plan` disables with the exact ratified reason before a round-trip.
 *
 * @returns {{valid: boolean, error: string|null, plannedIso: string|null}}
 *   `plannedIso` (ISO-8601 UTC ms, e.g. "2026-09-19T05:30:00.000Z" — the C2
 *   wire format) is populated ONLY when valid; never a guessed value on an
 *   invalid pair.
 */
export function validatePlanDischarge({ date, time, admittedAt, now = new Date() } = {}) {
  if (!date || !time) return { valid: false, error: null, plannedIso: null };
  const plannedIso = istInputValueToUtcIso(joinISTValue(date, time));
  if (!plannedIso) return { valid: false, error: null, plannedIso: null };
  const plannedMs = Date.parse(plannedIso);

  if (admittedAt) {
    const admittedMs = Date.parse(admittedAt);
    if (!Number.isNaN(admittedMs) && plannedMs < admittedMs) {
      return { valid: false, error: PLAN_DISCHARGE_BEFORE_ADMISSION_ERROR, plannedIso: null };
    }
  }

  const maxAheadMs = now.getTime() + PLAN_DISCHARGE_MAX_AHEAD_DAYS * 24 * 60 * 60 * 1000;
  if (plannedMs > maxAheadMs) {
    return { valid: false, error: PLAN_DISCHARGE_TOO_FAR_AHEAD_ERROR, plannedIso: null };
  }

  return { valid: true, error: null, plannedIso };
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

/**
 * formatDischargeDateTimeIST — UTC ISO instant -> "19 Sep 2026, 11:00 AM IST"
 * (the raw datetime portion, no label). Single source for every S4 surface
 * that needs the datetime string with its OWN prefix (FE-4's Complete-step
 * confirm line reads "Discharge date will be recorded as …", a different
 * sentence from the chip below) — never a second date/AM-PM formatter.
 */
export function formatDischargeDateTimeIST(iso) {
  if (!iso) return '';
  const utcMs = Date.parse(iso);
  if (Number.isNaN(utcMs)) return '';
  const ist = new Date(utcMs + IST_OFFSET_MS);
  const day = ist.getUTCDate();
  const month = MONTH_ABBR[ist.getUTCMonth()];
  const year = ist.getUTCFullYear();
  let hours = ist.getUTCHours();
  const minutes = String(ist.getUTCMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours %= 12;
  if (hours === 0) hours = 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm} IST`;
}

/**
 * The two chip prefixes + the Details-row one (DOD rule v3, stories.md
 * § Copy). Named constants so the ONE place that decides which of them a
 * surface gets is `dodIsConfirmed` below — never a re-typed sentence.
 */
export const PLANNED_DISCHARGE_CHIP_PREFIX = 'Planned discharge';
export const CONFIRMED_DISCHARGE_CHIP_PREFIX = 'Discharge';
export const DISCHARGE_DATE_ROW_PREFIX = 'Discharge date';

/**
 * formatPlannedDischargeChip — Screen C / desk-row chip text: "Planned
 * discharge: 19 Sep 2026, 11:00 AM IST". Returns '' for a null/unparseable
 * input so a caller can gate rendering on a plain truthiness check (no empty
 * chip — Screen C: "absent (no empty chip) when not [set]").
 */
export function formatPlannedDischargeChip(iso) {
  const dt = formatDischargeDateTimeIST(iso);
  return dt ? `${PLANNED_DISCHARGE_CHIP_PREFIX}: ${dt}` : '';
}

/**
 * plannedDischargeChipVisible — D3: a plan was never the DOD once the stay
 * is over, so the read-only chip must not linger on a discharged/terminal
 * admission's screens. ONE predicate, gated on `discharged_at` (the field
 * every "what was actually recorded" surface reads) plus `isTerminal(status)`
 * for the rare terminal row with no `discharged_at` (TRANSFERRED_OUT/
 * CANCELLED) — reused verbatim by every chip site (desk row, workspace
 * header, running-bill panel, final-bill view) so none of them can drift.
 */
export function plannedDischargeChipVisible(admission) {
  return !(admission && admission.discharged_at) && !isTerminal(admission && admission.status);
}

/**
 * plannedDischargeChipText — the ONE call every chip site makes (desk-row
 * rail item, workspace header, running bill ×2): resolves to `''` (no chip)
 * once `plannedDischargeChipVisible` says the stay is over, exactly like the
 * existing "unset/unparseable -> no chip" rule.
 *
 * DOD rule v3 (S3 AC5 / ruling 3 "no qualifier anywhere post-initiation"):
 * the SAME value re-labels itself the moment the date stops being a plan —
 * `dodIsConfirmed` (the ONE predicate, `constants/admission.js`) flips the
 * prefix from "Planned discharge:" to "Discharge:". The four chip sites call
 * THIS and nothing else, so none of them can ever say "planned" after an
 * initiate.
 */
export function plannedDischargeChipText(admission) {
  if (!plannedDischargeChipVisible(admission)) return '';
  const dt = formatDischargeDateTimeIST(admission && admission.planned_discharge_at);
  if (!dt) return '';
  return dodIsConfirmed(admission)
    ? `${CONFIRMED_DISCHARGE_CHIP_PREFIX}: ${dt}`
    : `${PLANNED_DISCHARGE_CHIP_PREFIX}: ${dt}`;
}

/**
 * dischargeDateRow — Screen B (Details tab) is the ONE surface that names the
 * field in full ("Discharge date: {date, time}", Copy table) rather than the
 * chip's short form, and the ONE that still states the date AFTER completion
 * (where every chip is gone, D3) with the correction-rail note. So it gets
 * its own selector — built on the SAME `dodIsConfirmed` predicate and the
 * SAME formatter as the chips, never a second date resolution.
 *
 * Returns `null` when there is nothing to state (no plan, no confirmed date,
 * no stamped discharge) — the row is absent, never an empty one.
 *
 * @returns {{heading: string, text: string, confirmed: boolean, completed: boolean}|null}
 */
export function dischargeDateRow(admission) {
  const a = admission || {};
  // Post-completion the RECORDED value is `discharged_at` — the plan/confirmed
  // attribute is no longer what any document prints.
  if (a.discharged_at) {
    const dt = formatDischargeDateTimeIST(a.discharged_at);
    return dt
      ? { heading: DISCHARGE_DATE_ROW_PREFIX, text: `${DISCHARGE_DATE_ROW_PREFIX}: ${dt}`, confirmed: true, completed: true }
      : null;
  }
  if (isTerminal(a.status)) return null; // CANCELLED / TRANSFERRED_OUT — nothing was recorded
  const dt = formatDischargeDateTimeIST(a.planned_discharge_at);
  if (!dt) return null;
  return dodIsConfirmed(a)
    ? { heading: DISCHARGE_DATE_ROW_PREFIX, text: `${DISCHARGE_DATE_ROW_PREFIX}: ${dt}`, confirmed: true, completed: false }
    : { heading: 'Discharge plan', text: `${PLANNED_DISCHARGE_CHIP_PREFIX}: ${dt}`, confirmed: false, completed: false };
}

/**
 * planDischargeErrorMessage — maps the C2 machine error codes to inline copy.
 * The two validation codes are pre-empted client-side (see
 * `validatePlanDischarge` above) — reachable here only on a stale-state race
 * (e.g. two tabs), so they reuse the SAME ratified sentence.
 *
 * Every code C2 can return is mapped to a plain sentence HERE: the server's
 * own `message` names internal permission slugs / field names and must never
 * reach the screen (contract reconcile, TPA-PREAUTH/lite-pr). The last four
 * sentences are pending business ratification — they are listed in
 * `integration.md`; nothing on screen says so.
 */
export function planDischargeErrorMessage(res) {
  const code = res && res.code;
  switch (code) {
    case PLAN_DISCHARGE_ERROR_CODE.PLANNED_DISCHARGE_BEFORE_ADMISSION:
      return PLAN_DISCHARGE_BEFORE_ADMISSION_ERROR;
    case PLAN_DISCHARGE_ERROR_CODE.PLANNED_DISCHARGE_TOO_FAR_AHEAD:
      return PLAN_DISCHARGE_TOO_FAR_AHEAD_ERROR;
    case PLAN_DISCHARGE_ERROR_CODE.INVALID_DATETIME:
      return 'Enter a valid planned discharge date and time.';
    case PLAN_DISCHARGE_ERROR_CODE.ADMISSION_TERMINAL:
      return 'This admission is closed — the discharge plan can no longer be changed.';
    case PLAN_DISCHARGE_ERROR_CODE.FIELD_NOT_PERMITTED:
      return 'You can change only the planned discharge date and time. Ask the front desk to change any other admission detail.';
    case PLAN_DISCHARGE_ERROR_CODE.NO_FIELDS:
      return 'Nothing to save — choose a planned discharge date and time first.';
    case PLAN_DISCHARGE_ERROR_CODE.IPD_TABLE_UNAVAILABLE:
      return 'Admissions are temporarily unavailable. Try again shortly.';
    default:
      return 'The discharge plan could not be saved. Try again.';
  }
}
